import React, { useState, useEffect, useCallback } from 'react';
import {
  MessageSquareText, Send, Users, BarChart3, Wallet,
  Loader2, CheckCircle, XCircle, Clock, ChevronLeft, ChevronRight,
  CreditCard
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../api/partnerApi';
import SMSContactsTab from '../../components/sms/SMSContactsTab';

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'send',      label: 'Send SMS',  icon: Send },
  { id: 'contacts',  label: 'Contacts',  icon: Users },
  { id: 'campaigns', label: 'Campaigns', icon: MessageSquareText },
  { id: 'wallet',    label: 'Wallet',    icon: Wallet },
];

const STATUS_COLORS = { sent:'bg-blue-100 text-blue-700', scheduled:'bg-violet-100 text-violet-700', sending:'bg-amber-100 text-amber-700', failed:'bg-red-100 text-red-700' };

function StatCard({ label, value, sub, color='blue', icon: Icon }) {
  const bg = { blue:'from-blue-500 to-blue-600', emerald:'from-emerald-500 to-emerald-600', red:'from-red-500 to-red-600', violet:'from-violet-500 to-violet-600', amber:'from-amber-500 to-amber-600' }[color];
  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5 flex gap-4 items-start">
      <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${bg} flex items-center justify-center flex-shrink-0`}><Icon className="w-5 h-5 text-white" /></div>
      <div><p className="text-xs font-bold text-slate-400 uppercase tracking-wide">{label}</p><p className="text-2xl font-extrabold text-slate-900 mt-0.5">{value ?? '—'}</p>{sub&&<p className="text-xs text-slate-400 mt-0.5">{sub}</p>}</div>
    </div>
  );
}

function AccessGate({ status, onRequest }) {
  const [req, setReq] = useState(false);
  if (status === 'pending') return (
    <div className="flex flex-col items-center py-20 space-y-4">
      <Clock className="w-12 h-12 text-amber-400" />
      <h2 className="text-xl font-bold text-slate-800">Request Pending</h2>
      <p className="text-slate-500 text-center max-w-sm">Awaiting approval from Master Admin.</p>
    </div>
  );
  return (
    <div className="flex flex-col items-center py-20 space-y-6 max-w-md mx-auto text-center">
      <div className="w-20 h-20 rounded-3xl bg-gradient-to-br from-blue-500 to-violet-600 flex items-center justify-center shadow-lg"><MessageSquareText className="w-10 h-10 text-white" /></div>
      <div><h2 className="text-2xl font-extrabold text-slate-800">SMS Marketing</h2><p className="text-slate-500 mt-2">Send bulk SMS campaigns to your referrals and marketing contacts.</p></div>
      <button onClick={async () => { setReq(true); await onRequest(); setReq(false); }} disabled={req}
        className="w-full py-3.5 rounded-xl bg-[#2563eb] text-white font-bold text-lg hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
        data-testid="partner-request-sms-btn">
        {req ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
        Request SMS Access
      </button>
    </div>
  );
}

function DashboardTab({ accessData }) {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get('/sms/analytics').then(r => setStats(r.data)).catch(()=>{}); }, []);
  const balance = accessData?.wallet?.balance ?? 0;
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
      <StatCard label="SMS Credits" value={balance} icon={Wallet} color="violet" />
      <StatCard label="Total Sent" value={stats?.total_sent??0} icon={Send} color="blue" />
      <StatCard label="Delivered" value={stats?.total_delivered??0} icon={CheckCircle} color="emerald" />
      <StatCard label="Failed" value={stats?.total_failed??0} icon={XCircle} color="red" />
      <StatCard label="Campaigns" value={stats?.total_campaigns??0} icon={MessageSquareText} color="amber" />
    </div>
  );
}

function SendTab({ accessData }) {
  const [msg, setMsg]       = useState('');
  const [name, setName]     = useState('');
  const [contacts, setContacts] = useState([]);
  const [selected, setSelected] = useState([]);
  const [manual, setManual] = useState('');
  const [mode, setMode]     = useState('contacts');
  const [sending, setSending] = useState(false);

  useEffect(() => { api.get('/sms/contacts?limit=100&page=1').then(r=>setContacts(r.data||[])).catch(()=>{}); }, []);

  const count = mode==='contacts'?selected.length:manual.split('\n').filter(l=>l.trim()).length;
  const balance = accessData?.wallet?.balance??0;
  const canAfford = balance >= count;
  const toggle = (id) => setSelected(s=>s.includes(id)?s.filter(x=>x!==id):[...s,id]);

  const handleSend = async () => {
    if (!msg.trim()) return toast.error('Message required');
    if (count===0) return toast.error('Add recipients');
    if (!canAfford) return toast.error('Insufficient credits');
    setSending(true);
    try {
      const r = await api.post('/sms/send', { message: msg, sender_name: name||undefined, recipient_type: mode,
        contact_ids: mode==='contacts'?selected:[], manual_numbers: mode==='manual'?manual.split('\n').filter(l=>l.trim()):[] });
      toast.success(r.message||'Sent!');
      setMsg(''); setSelected([]); setManual('');
    } catch (err) { toast.error(err?.response?.data?.detail||'Failed'); }
    finally { setSending(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      <div className="lg:col-span-3 space-y-4">
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-4">
          <h3 className="font-bold text-slate-800">Compose</h3>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Sender Name <span className="font-normal">(max 11)</span></label>
            <input value={name} onChange={e=>setName(e.target.value.slice(0,11))} placeholder="Eduva SMS" className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400 font-mono" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Message</label>
            <textarea value={msg} onChange={e=>setMsg(e.target.value)} rows={5} placeholder="Type your SMS…" className="w-full border border-slate-200 rounded-xl px-4 py-3 text-sm outline-none focus:border-blue-400 resize-none" />
            <div className="flex justify-between mt-1">
              <span className="text-xs text-slate-400">{msg.length} chars</span>
              <span className={`text-xs font-semibold ${canAfford?'text-emerald-600':'text-red-500'}`}>{count} credits needed ({balance} avail)</span>
            </div>
          </div>
          <button onClick={handleSend} disabled={sending||!msg.trim()||count===0||!canAfford}
            className="w-full py-3 rounded-xl font-bold text-white bg-[#2563eb] hover:bg-blue-700 disabled:opacity-40 transition-all flex items-center justify-center gap-2"
            data-testid="partner-send-sms-btn">
            {sending?<Loader2 className="w-4 h-4 animate-spin"/>:<Send className="w-4 h-4"/>}
            Send to {count} recipient{count!==1?'s':''}
          </button>
        </div>
      </div>
      <div className="lg:col-span-2">
        <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-5 space-y-3">
          <h3 className="font-bold text-slate-800">Recipients</h3>
          <div className="flex rounded-xl bg-slate-100 p-1 gap-1">
            {[['contacts','Contacts'],['manual','Manual']].map(([v,l])=>(
              <button key={v} onClick={()=>{setMode(v);setSelected([]);}}
                className={`flex-1 py-1.5 rounded-lg text-xs font-bold transition-all ${mode===v?'bg-white shadow text-slate-800':'text-slate-500'}`}>{l}</button>
            ))}
          </div>
          {mode==='contacts'&&(
            <div className="space-y-1 max-h-72 overflow-y-auto">
              <div className="flex justify-between mb-1"><span className="text-xs text-slate-500">{selected.length} selected</span><button onClick={()=>setSelected(contacts.map(c=>c.id))} className="text-xs text-blue-600 font-semibold">All</button></div>
              {contacts.map(c=>(
                <label key={c.id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 cursor-pointer">
                  <input type="checkbox" checked={selected.includes(c.id)} onChange={()=>toggle(c.id)} className="rounded" />
                  <div><p className="text-sm font-medium text-slate-700">{c.name||'Unnamed'}</p><p className="text-xs font-mono text-slate-400">{c.phone}</p></div>
                </label>
              ))}
              {contacts.length===0&&<p className="text-xs text-slate-400 text-center py-4">No contacts yet</p>}
            </div>
          )}
          {mode==='manual'&&(
            <div>
              <textarea value={manual} onChange={e=>setManual(e.target.value)} rows={7} placeholder={"One number per line:\n08012345678"} className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm font-mono outline-none resize-none" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



function CampaignsTab() {
  const [campaigns,setCampaigns]=useState([]);const [total,setTotal]=useState(0);const [page,setPage]=useState(1);const [loading,setLoading]=useState(false);
  const load=useCallback(async()=>{setLoading(true);try{const r=await api.get(`/sms/campaigns?page=${page}&limit=10`);setCampaigns(r.data||[]);setTotal(r.total||0);}finally{setLoading(false);};},[page]);
  useEffect(()=>{load();},[load]);
  const totalPages=Math.max(1,Math.ceil(total/10));
  return(
    <div className="space-y-4">
      {loading?<div className="flex justify-center py-16"><Loader2 className="w-5 h-5 animate-spin text-slate-300"/></div>:campaigns.length===0?<div className="text-center py-16 text-slate-400">No campaigns</div>:(
        <div className="space-y-3">{campaigns.map(c=>(
          <div key={c.id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-5">
            <div className="flex items-start justify-between gap-3 mb-3"><div><p className="font-bold text-slate-800">{c.name}</p><p className="text-xs text-slate-400">{c.sent_at?new Date(c.sent_at).toLocaleString('en-NG'):'Pending'}</p></div><span className={`text-xs font-bold px-2.5 py-1 rounded-full ${STATUS_COLORS[c.status]||'bg-slate-100 text-slate-500'}`}>{c.status}</span></div>
            <p className="text-sm text-slate-600 bg-slate-50 rounded-xl px-4 py-2.5 mb-3 line-clamp-2">{c.message}</p>
            <div className="flex gap-4 text-xs"><span className="flex items-center gap-1 text-blue-600 font-semibold"><Send className="w-3 h-3"/>{c.stats?.sent??0}</span><span className="flex items-center gap-1 text-emerald-600 font-semibold"><CheckCircle className="w-3 h-3"/>{c.stats?.delivered??0}</span><span className="flex items-center gap-1 text-red-500 font-semibold"><XCircle className="w-3 h-3"/>{c.stats?.failed??0}</span></div>
          </div>
        ))}</div>
      )}
      {totalPages>1&&(<div className="flex items-center justify-center gap-2"><button onClick={()=>setPage(p=>Math.max(1,p-1))} disabled={page===1} className="p-2 rounded-xl border border-slate-200 disabled:opacity-40"><ChevronLeft className="w-4 h-4"/></button><span className="text-sm">{page}/{totalPages}</span><button onClick={()=>setPage(p=>Math.min(totalPages,p+1))} disabled={page===totalPages} className="p-2 rounded-xl border border-slate-200 disabled:opacity-40"><ChevronRight className="w-4 h-4"/></button></div>)}
    </div>
  );
}

function WalletTab({ pricing }) {
  const [wallet,setWallet]=useState(null);const [txns,setTxns]=useState([]);const [units,setUnits]=useState('');const [topping,setTopping]=useState(false);
  useEffect(()=>{ api.get('/sms/wallet').then(r=>{setWallet(r.data?.wallet);setTxns(r.data?.transactions||[]);}).catch(()=>{}); },[]);

  const pricePerCredit = pricing?.price_per_credit_naira ?? 2;
  const txFee          = pricing?.transaction_fee_naira ?? 0;
  const minUnits       = pricing?.min_topup_units ?? 100;
  const unitsNum       = parseInt(units) || 0;
  const subtotal       = unitsNum * pricePerCredit;
  const totalPay       = subtotal + txFee;

  const topup=async()=>{
    if(!unitsNum||unitsNum<minUnits)return toast.error(`Minimum topup is ${minUnits} credits`);
    setTopping(true);
    try{
      const cb=`${window.location.origin}/reseller/sms?topup=success`;
      const r=await api.post('/sms/wallet/topup/initialize',{units:unitsNum,callback_url:cb});
      window.location.href=r.data?.checkout_url;
    }catch(err){toast.error(err?.response?.data?.detail||'Failed');}
    finally{setTopping(false);}
  };
  return(
    <div className="max-w-xl space-y-6">
      <div className="bg-gradient-to-br from-[#2563eb] to-violet-600 rounded-3xl p-6 text-white">
        <p className="text-sm font-bold text-blue-200 uppercase">SMS Credits</p>
        <p className="text-5xl font-extrabold mt-1">{wallet?.balance??0}</p>
        <p className="text-blue-200 text-sm mt-1">credits balance</p>
      </div>
      <div className="bg-white rounded-3xl border border-slate-100 shadow-sm p-6 space-y-4">
        <h3 className="font-bold text-slate-800">Top Up</h3>
        <div>
          <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">Number of units</label>
          <input type="number" min={minUnits} step="1" value={units} onChange={e=>setUnits(e.target.value)}
            placeholder={`e.g. ${minUnits}`} className="w-full border border-slate-200 rounded-xl px-4 py-2.5 text-sm outline-none focus:border-blue-400"
            data-testid="partner-topup-units-input" />
        </div>
        {unitsNum>0&&(
          <div className="bg-slate-50 rounded-2xl p-4 space-y-2 text-sm border border-slate-100">
            <div className="flex justify-between"><span className="text-slate-500">Unit price</span><span className="font-semibold">₦{pricePerCredit.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-slate-500">Amount to pay</span><span className="font-semibold">₦{subtotal.toFixed(2)}</span></div>
            {txFee>0&&<div className="flex justify-between"><span className="text-slate-500">Transaction fee</span><span className="font-semibold">₦{txFee.toFixed(2)}</span></div>}
            <div className="flex justify-between pt-2 border-t border-slate-200"><span className="font-bold text-slate-800">Total payment</span><span className="font-bold text-[#2563eb] text-base">₦{totalPay.toFixed(2)}</span></div>
          </div>
        )}
        <button onClick={topup} disabled={topping||!unitsNum||unitsNum<minUnits}
          className="w-full py-3 rounded-xl bg-emerald-500 text-white font-bold hover:bg-emerald-600 disabled:opacity-40 transition-all flex items-center justify-center gap-2"
          data-testid="partner-topup-btn">
          {topping?<Loader2 className="w-4 h-4 animate-spin"/>:<CreditCard className="w-4 h-4"/>}
          Pay ₦{unitsNum>0?totalPay.toFixed(2):'—'} for {unitsNum||0} credits
        </button>
        <p className="text-xs text-slate-400 text-center">Minimum: {minUnits} credits</p>
      </div>
      {txns.length>0&&(<div className="bg-white rounded-3xl border border-slate-100 shadow-sm overflow-hidden"><div className="px-5 py-3.5 border-b border-slate-50 font-bold text-slate-700 text-sm">Transactions</div><div className="divide-y divide-slate-50">{txns.map(t=>(<div key={t.id} className="flex items-center justify-between px-5 py-3.5"><div><p className="text-sm font-medium text-slate-700">{t.description}</p><p className="text-xs text-slate-400">{new Date(t.created_at).toLocaleString('en-NG')}</p></div><span className={`text-sm font-bold ${t.type==='topup'?'text-emerald-600':'text-red-500'}`}>{t.type==='topup'?'+':'-'}{t.units}</span></div>))}</div></div>)}
    </div>
  );
}

export default function PartnerSMSPage() {
  const [tab,setTab]=useState('dashboard');const [accessData,setAccessData]=useState(null);const [loading,setLoading]=useState(true);
  const loadAccess=useCallback(async()=>{setLoading(true);try{const r=await api.get('/sms/access-status');setAccessData(r.data);}catch{setAccessData(null);}finally{setLoading(false);};},[]);
  useEffect(()=>{loadAccess();},[loadAccess]);
  const requestAccess=async()=>{await api.post('/sms/request-access');toast.success('Request submitted!');loadAccess();};
  if(loading)return<div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-300"/></div>;
  const hasAccess=accessData?.has_access;const reqStatus=accessData?.access_request?.status;
  return(
    <div className="px-6 md:px-8 py-6 md:py-8" data-testid="partner-sms-page">
      <div className="relative bg-[#0f172a] rounded-3xl overflow-hidden mb-7" style={{minHeight:100}}>
        <div className="absolute inset-0 opacity-5" style={{backgroundImage:'radial-gradient(circle, white 1px, transparent 1px)',backgroundSize:'28px 28px'}}/>
        <div className="relative p-7 flex items-center gap-4">
          <div className="w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center"><MessageSquareText className="w-5 h-5 text-white"/></div>
          <div><h1 className="text-2xl font-extrabold text-white tracking-tight">SMS Marketing</h1><p className="text-slate-400 text-sm mt-0.5">Send bulk SMS to your contacts and referrals</p></div>
          {hasAccess&&accessData?.wallet&&(<div className="ml-auto flex items-center gap-2 bg-white/10 rounded-xl px-3 py-2"><Wallet className="w-4 h-4 text-white/60"/><span className="text-white font-bold text-sm">{accessData.wallet.balance} credits</span></div>)}
        </div>
      </div>
      {!hasAccess?<AccessGate status={reqStatus} onRequest={requestAccess}/>:(
        <>
          <div className="bg-slate-100 rounded-2xl p-1 flex gap-1 mb-7 overflow-x-auto">
            {TABS.map(t=>(<button key={t.id} onClick={()=>setTab(t.id)} className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold whitespace-nowrap transition-all ${tab===t.id?'bg-white text-slate-800 shadow':'text-slate-500 hover:text-slate-700'}`} data-testid={`partner-sms-tab-${t.id}`}><t.icon className="w-4 h-4"/>{t.label}</button>))}
          </div>
          {tab==='dashboard'&&<DashboardTab accessData={accessData}/>}
          {tab==='send'&&<SendTab accessData={accessData}/>}
          {tab==='contacts'&&<SMSContactsTab contactsApiBase="/sms" apiClient={api} />}
          {tab==='campaigns'&&<CampaignsTab/>}
          {tab==='wallet'&&<WalletTab pricing={accessData?.pricing}/>}
        </>
      )}
    </div>
  );
}
